import { IElemSizes, IElement } from 'common/interfaces';
import { ResizeHandlerPositions } from 'hooks/hooks.interfaces';
import useGroupResize from 'hooks/useGroupResize';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import CollectionService from 'services/CollectionService';

import useGroupDragAndDrop from './useGroupDragAndDrop';
import useGroupSelection from './useGroupSelection';

export type TypeResizeHandler = (
  event: React.MouseEvent<Element, MouseEvent>,
) => void;

interface IUseEventHandlers {
  elemSize: IElemSizes;
  styles: React.CSSProperties;
  isBlocked: boolean;
  elemRef: React.RefObject<HTMLDivElement>;
  rotatedElemRef: React.RefObject<HTMLDivElement>;
  isSelectionWrapperVisible: boolean;
  resizeHandlers: {
    top: TypeResizeHandler;
    right: TypeResizeHandler;
    bottom: TypeResizeHandler;
    left: TypeResizeHandler;
    topRight: TypeResizeHandler;
    bottomRight: TypeResizeHandler;
    bottomLeft: TypeResizeHandler;
    topLeft: TypeResizeHandler;
  };
}

export default function useGroupEventHandlers(id: string): IUseEventHandlers {
  const dispatch = useDispatch();

  const [elemSize, setElemSize] = useState<IElemSizes>({
    width: 0,
    height: 0,
  });

  const [groupElem, setGroupElem] = useState<IElement | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);

  const elemRef = useRef<HTMLDivElement>(null);
  const rotatedElemRef = useRef<HTMLDivElement>(null);

  const elems = useTypedSelector((state) => state.elems.elems);
  const pressedKeys = useTypedSelector((state) => state.common.pressedKeys);
  const selectedElemsIds = useTypedSelector(
    (state) => state.elems.selectedElemsIds,
  );

  const styles = useMemo(() => {
    const elem = CollectionService.findElemById(elems, id);

    if (!elem) return {} as React.CSSProperties;

    setGroupElem(elem);

    setElemSize({
      width: Math.round(parseInt(String(elem.styles.width))),
      height: Math.round(parseInt(String(elem.styles.height))),
    });

    if (elem.isHidden) {
      return { ...elem.styles, display: 'none' };
    }

    setIsBlocked(elem.isBlocked);

    return elem.styles;
  }, [elems, id]);

  const { isDragAndDropAction } = useGroupDragAndDrop(
    id,
    elemRef,
    elems,
    pressedKeys,
    dispatch,
  );

  const { isSelected } = useGroupSelection(
    id,
    elemRef,
    elems,
    pressedKeys,
    selectedElemsIds,
    dispatch,
  );

  const bottomRightResizeHandler = useGroupResize(
    groupElem,
    elemRef,
    pressedKeys,
    setElemSize,
    ResizeHandlerPositions.BOTTOM_RIGHT_CORNER,
  );

  const topLeftResizeHandler = useGroupResize(
    groupElem,
    elemRef,
    pressedKeys,
    setElemSize,
    ResizeHandlerPositions.TOP_LEFT_CORNER,
  );

  const bottomLeftResizeHandler = useGroupResize(
    groupElem,
    elemRef,
    pressedKeys,
    setElemSize,
    ResizeHandlerPositions.BOTTOM_LEFT_CORNER,
  );

  const topRightResizeHandler = useGroupResize(
    groupElem,
    elemRef,
    pressedKeys,
    setElemSize,
    ResizeHandlerPositions.TOP_RIGHT_CORNER,
  );

  const topSideResizeHandler = useGroupResize(
    groupElem,
    elemRef,
    pressedKeys,
    setElemSize,
    ResizeHandlerPositions.TOP_SIDE,
  );

  const bottomSideResizeHandler = useGroupResize(
    groupElem,
    elemRef,
    pressedKeys,
    setElemSize,
    ResizeHandlerPositions.BOTTOM_SIDE,
  );

  const leftSideResizeHandler = useGroupResize(
    groupElem,
    elemRef,
    pressedKeys,
    setElemSize,
    ResizeHandlerPositions.LEFT_SIDE,
  );

  const rightSideResizeHandler = useGroupResize(
    groupElem,
    elemRef,
    pressedKeys,
    setElemSize,
    ResizeHandlerPositions.RIGHT_SIDE,
  );

  const isSelectionWrapperVisible =
    isSelected && selectedElemsIds.length <= 1 && !isDragAndDropAction;

  return {
    styles,
    isBlocked,
    elemRef,
    elemSize,
    rotatedElemRef,
    isSelectionWrapperVisible,
    resizeHandlers: {
      top: topSideResizeHandler,
      right: rightSideResizeHandler,
      bottom: bottomSideResizeHandler,
      left: leftSideResizeHandler,
      topRight: topRightResizeHandler,
      bottomRight: bottomRightResizeHandler,
      bottomLeft: bottomLeftResizeHandler,
      topLeft: topLeftResizeHandler,
    },
  };
}
